import { Star, Sparkles, CheckCircle, FileText, Loader2, Wand2, Languages } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import type { Review } from '@/lib/api/reviews'
import { formatDistanceToNow } from '@/lib/format'
import { getPlatformInfo } from '@/lib/platform'
import { useReviewLanguage } from '@/hooks/use-review-language'
import { useTranslation } from 'react-i18next'

export type Sentiment = 'positive' | 'neutral' | 'negative'

export interface ReviewCardProps {
  review: Review
  isSelected?: boolean
  isGenerating?: boolean
  onClick?: () => void
  onGenerateResponse?: () => void
}

function getSentimentFromRating(rating: number): Sentiment {
  if (rating >= 4) return 'positive'
  if (rating <= 2) return 'negative'
  return 'neutral'
}

function getSentimentColor(sentiment: Sentiment) {
  switch (sentiment) {
    case 'positive':
      return 'bg-accent/10 text-accent border-accent'
    case 'neutral':
      return 'bg-chart-4/10 text-chart-4 border-chart-4'
    case 'negative':
      return 'bg-primary/10 text-primary border-primary'
  }
}

function getSentimentBar(sentiment: Sentiment) {
  switch (sentiment) {
    case 'positive':
      return 'bg-accent'
    case 'neutral':
      return 'bg-chart-4'
    case 'negative':
      return 'bg-primary'
  }
}

export function ReviewCard({
  review,
  isSelected = false,
  isGenerating = false,
  onClick,
  onGenerateResponse,
}: ReviewCardProps) {
  const { t } = useTranslation()

  const formatRelativeTime = (dateString: string | null | undefined): string => {
    if (!dateString) return t('reviews.unknownDate')
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true })
    } catch {
      return dateString
    }
  }

  // Prefer API-provided sentiment when available, fall back to rating-derived
  const apiSentiment = review.analysis?.sentiment?.toLowerCase() as Sentiment | undefined
  const ratingsentiment = getSentimentFromRating(review.rating)
  const sentiment: Sentiment =
    apiSentiment === 'positive' || apiSentiment === 'neutral' || apiSentiment === 'negative'
      ? apiSentiment
      : ratingsentiment

  const platform = getPlatformInfo(review.provider)
  const hasResponse = !!review.owner_response
  const hasAISuggestion = review.rating !== 3 && !hasResponse

  const {
    text: displayedText,
    canToggle: canToggleLanguage,
    showingOriginal,
    toggle: toggleLanguage,
  } = useReviewLanguage(review)

  const formatSentimentLabel = (raw: string) => {
    const key = raw.toLowerCase()
    if (key === 'positive' || key === 'neutral' || key === 'negative') {
      return t(`reviews.sentiment.${key}`)
    }
    const formatted = raw.replace(/_/g, ' ')
    return formatted.charAt(0).toUpperCase() + formatted.slice(1)
  }

  const sentimentLabel = review.analysis
    ? `${formatSentimentLabel(review.analysis.sentiment)} (${review.analysis.sentiment_score.toFixed(2)})`
    : formatSentimentLabel(sentiment)

  return (
    <Card
      className={cn(
        'relative overflow-hidden cursor-pointer transition-colors duration-150 shadow-none',
        isSelected ? 'bg-primary/[0.06] border-primary/30' : 'border-border/60 hover:bg-muted/40'
      )}
      onClick={onClick}
    >
      <div className={cn('absolute left-0 inset-y-0 w-[3px]', getSentimentBar(sentiment))} />
      <CardContent className="p-4 pl-5">
        <div className="flex items-start gap-3">
          <Avatar className="h-9 w-9 flex-shrink-0">
            <AvatarFallback className="bg-gradient-to-br from-accent/30 to-primary/30 text-sm font-medium">
              {review.author_name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 space-y-2.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm">{review.author_name}</span>
              <Badge
                variant="outline"
                className={`${platform.color} text-white border-0 text-xs px-2 py-0`}
              >
                {platform.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(review.published_at)}
              </span>
              {hasResponse && (
                <Badge
                  variant="outline"
                  className="text-xs bg-accent/10 text-accent border-accent/30 flex items-center gap-1"
                >
                  <CheckCircle className="h-2.5 w-2.5" />
                  {t('reviews.badge.responded')}
                </Badge>
              )}
              {review.has_draft && (
                <Badge
                  variant="outline"
                  className="text-xs bg-chart-4/10 text-chart-4 border-chart-4/30 flex items-center gap-1"
                >
                  <FileText className="h-2.5 w-2.5" />
                  {t('reviews.badge.draft')}
                </Badge>
              )}
              {hasAISuggestion && !review.has_draft && (
                <Badge
                  variant="outline"
                  className="text-xs bg-primary/10 text-primary border-primary/30 flex items-center gap-1"
                >
                  <Sparkles className="h-2.5 w-2.5" />
                  {t('reviews.badge.aiReady')}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-3.5 w-3.5 ${
                      star <= review.rating ? 'fill-primary text-primary' : 'text-muted-foreground'
                    }`}
                  />
                ))}
                <span className="text-sm font-medium ml-1">{review.rating}.0</span>
              </div>

              <Badge variant="outline" className={`text-xs ${getSentimentColor(sentiment)}`}>
                {sentimentLabel}
              </Badge>
            </div>

            {displayedText && (
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                {displayedText}
              </p>
            )}

            {canToggleLanguage && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  toggleLanguage()
                }}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                <Languages className="h-3 w-3" />
                {showingOriginal ? t('reviews.showTranslation') : t('reviews.showOriginal')}
              </button>
            )}

            {review.owner_response && (
              <div className="mt-2 pt-2 border-t border-border/50">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">{t('reviews.yourResponse')}</span>{' '}
                  <span className="line-clamp-1">{review.owner_response}</span>
                </p>
              </div>
            )}

            {onGenerateResponse && !hasResponse && (
              <div className="pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={isGenerating}
                  onClick={(e) => {
                    e.stopPropagation()
                    onGenerateResponse()
                  }}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      {t('reviews.generating')}
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-3 w-3 mr-1" />
                      {t('reviews.generateResponse')}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
